import { FC, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { DraggableCapabilityItem } from './draggable-capability-item';
import type {
  Cut,
  CutsheetCatalog,
  CutsheetQuestionnaire,
  Primal,
} from '../../types';

// ----- Group Card -----
interface GroupCardProps {
  primal: Primal;
  cuts: Cut[];
  onEditCut: (cut: Cut) => void;
  onEditGroup?: (primal: Primal) => void;
  isItemUsed: (
    id: string,
    type: 'cut' | 'trim' | 'extra' | 'specification',
  ) => boolean;
}

const GroupCard: FC<GroupCardProps> = ({
  primal,
  cuts,
  onEditCut,
  onEditGroup,
  isItemUsed,
}) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
    {/* Group header */}
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        px: 1,
        py: 0.75,
        bgcolor: 'grey.200',
        borderBottom: cuts.length > 0 ? '1px solid' : 'none',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
        {primal.title}
      </Typography>
      {onEditGroup && (
        <IconButton size="small" onClick={() => onEditGroup(primal)}>
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>

    {/* Cuts in this group */}
    {cuts.length > 0 && (
      <Stack spacing={0} sx={{ p: 0.5 }}>
        {cuts.map((cut) => (
          <DraggableCapabilityItem
            key={cut.id}
            item={cut}
            type="cut"
            onEdit={() => onEditCut(cut)}
            isUsed={isItemUsed(cut.id, 'cut')}
          />
        ))}
      </Stack>
    )}
  </Paper>
);

export interface CapabilitiesPanelProps {
  capabilities: CutsheetCatalog;
  questionnaire: CutsheetQuestionnaire;
  /** When true the panel enters locked-scroll mode to avoid conflicts with drag. */
  isDraggingCapability: boolean;
  onAddGroup?: () => void;
  onEditGroup?: (primal: Primal) => void;
  onAddCut: () => void;
  onEditCut: (cut: Cut) => void;
  onAddTrim: () => void;
  onEditTrim: (trim: any) => void;
  onAddExtra: () => void;
  onEditExtra: (extra: any) => void;
  onAddSpecification: () => void;
  onEditSpecification: (spec: any) => void;
}

/**
 * Left-hand capabilities palette used in the unified cutsheet builder.
 * Renders draggable cards for cuts (grouped by primal), trim, extras, and
 * specifications, plus a filter field and an "Add" menu.
 * @param root0
 * @param root0.capabilities
 * @param root0.questionnaire
 * @param root0.isDraggingCapability
 * @param root0.onAddCut
 * @param root0.onEditCut
 * @param root0.onAddTrim
 * @param root0.onEditTrim
 * @param root0.onAddExtra
 * @param root0.onEditExtra
 * @param root0.onAddSpecification
 * @param root0.onEditSpecification
 * @param root0.onAddGroup
 */
export const CapabilitiesPanel: FC<CapabilitiesPanelProps> = ({
  capabilities,
  questionnaire,
  isDraggingCapability,
  onAddGroup,
  onEditGroup,
  onAddCut,
  onEditCut,
  onAddTrim,
  onEditTrim,
  onAddExtra,
  onEditExtra,
  onAddSpecification,
  onEditSpecification,
}) => {
  const [filterText, setFilterText] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  /**
   * Returns true if a capability item is referenced by at least one question.
   * @param itemId
   * @param itemType
   */
  const isItemUsed = (
    itemId: string,
    itemType: 'cut' | 'trim' | 'extra' | 'specification',
  ): boolean =>
    questionnaire.questions.some((question) => {
      if (question.type === 'free_selection') {
        return question.availableCutIds?.includes(itemId);
      }
      if (question.type === 'choice_group') {
        return question.options?.some((option: any) =>
          option.cutIds?.includes(itemId),
        );
      }
      if (
        question.type === 'trim_ranking' ||
        question.type === 'trim_percentage'
      ) {
        return question.availableTrimIds?.includes(itemId);
      }
      if (question.type === 'extras_selection') {
        return question.availableExtraIds?.includes(itemId);
      }
      if (question.type === 'global_spec') {
        return question.specificationId === itemId;
      }
      return false;
    });

  const filteredCuts = capabilities.cuts.filter((cut) =>
    cut.title.toLowerCase().includes(filterText.toLowerCase()),
  );
  const filteredTrim = capabilities.trim.filter((trim) =>
    trim.title.toLowerCase().includes(filterText.toLowerCase()),
  );
  const filteredSpecs = capabilities.specifications.filter((spec) =>
    spec.title.toLowerCase().includes(filterText.toLowerCase()),
  );

  // Group cuts by group
  const cutsByPrimal = filteredCuts.reduce(
    (acc, cut) => {
      const groupId = cut.groupId || 'unassigned';
      if (!acc[groupId]) acc[groupId] = [];
      acc[groupId].push(cut);
      return acc;
    },
    {} as Record<string, Cut[]>,
  );

  return (
    <Box
      sx={{
        height: 'calc(100vh - 108px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Fixed header area — left flush with the tab bar; right inset by
          the scrollbar width so the Add button lines up with the group
          cards' right edge (the scrollbar lives at the column's border on
          the scroll box below). */}
      <Box
        sx={{
          pl: 0,
          pb: 2,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ width: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter catalog..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            size="small"
            sx={{ px: 2 }}
          >
            Add
          </Button>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {onAddGroup && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onAddGroup();
              }}
            >
              <Stack>
                <Typography variant="inherit">Group</Typography>
                <Typography variant="caption" color="text.secondary">
                  Add a new group (e.g. Chuck, Brisket)
                </Typography>
              </Stack>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onAddCut();
            }}
          >
            <Stack>
              <Typography variant="inherit">Cut</Typography>
              <Typography variant="caption" color="text.secondary">
                Add a new cut option
              </Typography>
            </Stack>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onAddTrim();
            }}
          >
            <Stack>
              <Typography variant="inherit">Trim</Typography>
              <Typography variant="caption" color="text.secondary">
                Add a trim option
              </Typography>
            </Stack>
          </MenuItem>

          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onAddSpecification();
            }}
          >
            <Stack>
              <Typography variant="inherit">Specification</Typography>
              <Typography variant="caption" color="text.secondary">
                Add a specification question
              </Typography>
            </Stack>
          </MenuItem>
        </Menu>
      </Box>

      {/* Scrollable content area — no horizontal padding; the scrollbar
          sits at the column's right border. Cards fill the available
          width (column - scrollbar), and the header above uses matching
          `pr: '14px'` so the Add button's right edge lines up with the
          cards' right edge. */}
      <Box
        sx={{
          flex: 1,
          overflow: isDraggingCapability ? 'hidden' : 'auto',
          px: 0,
          pt: 0,
          pb: 2,
        }}
      >
        <Stack spacing={2}>
          {/* Groups with cuts */}
          {capabilities.primals.map((primal) => {
            const cuts = cutsByPrimal[primal.id] || [];
            if (
              filterText &&
              cuts.length === 0 &&
              !primal.title.toLowerCase().includes(filterText.toLowerCase())
            ) {
              return null;
            }
            return (
              <GroupCard
                key={primal.id}
                primal={primal}
                cuts={cuts}
                onEditCut={onEditCut}
                onEditGroup={onEditGroup}
                isItemUsed={isItemUsed}
              />
            );
          })}

          {/* Trim */}
          {filteredTrim.length > 0 && (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  px: 1,
                  py: 0.75,
                  bgcolor: 'grey.200',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  Trim
                </Typography>
              </Stack>
              <Stack spacing={0} sx={{ p: 0.5 }}>
                {filteredTrim.map((trim) => (
                  <DraggableCapabilityItem
                    key={trim.id}
                    item={trim}
                    type="trim"
                    onEdit={() => onEditTrim(trim)}
                    isUsed={isItemUsed(trim.id, 'trim')}
                  />
                ))}
              </Stack>
            </Paper>
          )}

          {/* Specifications */}
          {filteredSpecs.length > 0 && (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  px: 1,
                  py: 0.75,
                  bgcolor: 'grey.200',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ flex: 1 }}
                >
                  Specifications
                </Typography>
              </Stack>
              <Stack spacing={0} sx={{ p: 0.5 }}>
                {filteredSpecs.map((spec) => (
                  <DraggableCapabilityItem
                    key={spec.id}
                    item={spec}
                    type="specification"
                    onEdit={() => onEditSpecification(spec)}
                    isUsed={isItemUsed(spec.id, 'specification')}
                  />
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
